import * as THREE from 'three';
import { CAMERA } from '../core/SimConfig.js';

const _offset = new THREE.Vector3();
const _target = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _matrix = new THREE.Matrix4();

/**
 * Start-screen orbit camera (CameraMode.START).
 *
 * OrbitControls-style: left-drag rotates, scroll zooms, and the view gently
 * auto-rotates around the parked A-10 when idle. Unlike the in-game cameras it
 * owns its own mouse listeners (no pointer lock) and produces a position +
 * quaternion that CameraManager copies onto the shared camera — so it stays a
 * peer of ChaseCamera / EnemyCamera rather than fighting them for the camera.
 */
export default class StartCamera {
  constructor({ canvas, aircraft }) {
    this.canvas = canvas;
    this.aircraft = aircraft;

    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    const cfg = CAMERA.start;
    // Seed yaw behind the aircraft's spawn heading for a flattering 3/4 view.
    const heading = aircraft?.quaternion
      ? Math.atan2(
          2 * (aircraft.quaternion.w * aircraft.quaternion.y),
          1 - 2 * aircraft.quaternion.y * aircraft.quaternion.y,
        )
      : 0;
    this.targetYaw = heading + cfg.yawOffset;
    this.yaw = this.targetYaw;
    this.targetPitch = cfg.pitch;
    this.pitch = cfg.pitch;
    this.targetDistance = cfg.distance;
    this.distance = cfg.distance;

    this._dragging = false;
    this._idleTime = cfg.idleDelay; // start auto-rotating immediately
    this._spinVelocity = 0; // rad/s of mouse-motion yaw spin; decays over time
    this._pitchVelocity = 0; // rad/s of mouse-motion pitch spin; decays over time
    this._enabled = false;

    this._onMouseDown = (e) => {
      if (e.button !== 0) return;
      this._dragging = true;
      this._idleTime = 0;
    };
    this._onMouseMove = (e) => {
      const cfg = CAMERA.start;
      // Any mouse motion imparts an orbit "spin" velocity that eases off over
      // time — faster motion = stronger push. This is layered on top of the
      // click-drag and auto-rotate behaviours (handled below / in update()).
      // Mouse-right drives the orbit left, mouse-down tilts it up — matching
      // the drag direction on each axis.
      this._spinVelocity -= e.movementX * cfg.moveInfluence;
      this._spinVelocity = Math.max(-cfg.maxSpinSpeed, Math.min(cfg.maxSpinSpeed, this._spinVelocity));
      this._pitchVelocity -= e.movementY * cfg.moveInfluence;
      this._pitchVelocity = Math.max(-cfg.maxSpinSpeed, Math.min(cfg.maxSpinSpeed, this._pitchVelocity));

      if (!this._dragging) return;
      const s = cfg.dragSensitivity;
      this.targetYaw -= e.movementX * s;
      this.targetPitch -= e.movementY * s;
      this.targetPitch = Math.max(cfg.minPitch, Math.min(cfg.maxPitch, this.targetPitch));
      this._idleTime = 0;
    };
    this._onMouseUp = () => {
      // Only an actual orbit drag should reset the idle timer. A plain click on
      // a UI panel still bubbles a document mouseup here; ignoring it keeps the
      // auto-rotation running.
      if (!this._dragging) return;
      this._dragging = false;
      this._idleTime = 0;
    };
    this._onWheel = (e) => {
      const f = e.deltaY < 0 ? CAMERA.start.zoomFactor : 1 / CAMERA.start.zoomFactor;
      this.targetDistance = Math.max(
        CAMERA.start.minDistance,
        Math.min(CAMERA.start.maxDistance, this.targetDistance * f),
      );
      this._idleTime = 0;
      e.preventDefault();
    };
  }

  /** Begin listening for orbit input (called by CameraManager on START enter). */
  enable() {
    if (this._enabled) return;
    this._enabled = true;
    this.canvas.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
    this.canvas.addEventListener('wheel', this._onWheel, { passive: false });
  }

  /** Stop listening (called when leaving START mode). */
  disable() {
    if (!this._enabled) return;
    this._enabled = false;
    this._dragging = false;
    this.canvas.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.canvas.removeEventListener('wheel', this._onWheel);
  }

  update(dt) {
    const cfg = CAMERA.start;

    // Idle auto-rotate: drift the target yaw once input has been quiet a while.
    if (!this._dragging) {
      this._idleTime += dt;
      if (this._idleTime >= cfg.idleDelay) this.targetYaw += cfg.autoRotateSpeed * dt;
    }

    // Mouse-motion spin: integrate the decaying velocities into the target
    // yaw/pitch, then ease them back toward zero so the orbit coasts to a stop.
    // Pitch stays clamped to its limits to keep the camera above the ramp.
    this.targetYaw += this._spinVelocity * dt;
    this.targetPitch += this._pitchVelocity * dt;
    this.targetPitch = Math.max(cfg.minPitch, Math.min(cfg.maxPitch, this.targetPitch));
    const decay = Math.exp(-cfg.moveDamping * dt);
    this._spinVelocity *= decay;
    this._pitchVelocity *= decay;

    // Ease orbit angles + zoom toward their targets.
    const alpha = 1 - Math.exp(-cfg.lookSmoothing * dt);
    this.yaw += (this.targetYaw - this.yaw) * alpha;
    this.pitch += (this.targetPitch - this.pitch) * alpha;
    this.distance += (this.targetDistance - this.distance) * alpha;

    // Focal point: aircraft origin lifted to roughly canopy height.
    _target.copy(this.aircraft.position);
    _target.y += cfg.targetHeight;

    const cosP = Math.cos(this.pitch);
    _offset.set(
      Math.sin(this.yaw) * cosP,
      -Math.sin(this.pitch),
      Math.cos(this.yaw) * cosP,
    );
    this.position.copy(_target).addScaledVector(_offset, this.distance);

    // Shift the look-at target along the camera's screen-left axis so the
    // aircraft sits right-of-center, clearing the left-side UI panel. Scaled by
    // distance so the on-screen offset stays roughly constant across zoom.
    _forward.copy(_target).sub(this.position).normalize();
    _right.crossVectors(_forward, THREE.Object3D.DEFAULT_UP).normalize();
    _target.addScaledVector(_right, -cfg.targetLateralOffset * this.distance);

    _matrix.lookAt(this.position, _target, THREE.Object3D.DEFAULT_UP);
    this.quaternion.setFromRotationMatrix(_matrix);
  }

  dispose() {
    this.disable();
    this.aircraft = null;
  }
}
